import React, { useCallback, useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  BriefcaseBusiness,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Users,
  Download,
  Eye,
} from "lucide-react";

import PartnerHeader from "../../component/PartnerHeader";
import PartnerFooter from "../../component/PartnerFooter";
import jobService from "../../services/JobService";
import {
  getActiveSubscription,
  getActiveFeatureUsage,
} from "../../services/subscriptionProductService";

const API_BASE = "/api";

const THEME = {
  primary: "#3E0F8D",
  secondary: "#9564DD",
  accent: "#E4DA72",
  neutral: "#EEEEEE",
};

const COLORS = [
  THEME.primary,
  THEME.secondary,
  THEME.accent,
  "#6B3FB0",
  "#B8A9E0",
  "#F0E9A8",
];

const firstValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return null;
};

const getJobId = (job) =>
  firstValue(job?.jobId, job?.job_id, job?.id);

const getPostedById = (job) => {
  const postedBy = firstValue(
    job?.postedBy,
    job?.posted_by,
    job?.postedById,
    job?.posted_by_id
  );

  if (postedBy && typeof postedBy === "object") {
    return firstValue(
      postedBy.id,
      postedBy.userId,
      postedBy.partnerId
    );
  }

  return postedBy;
};

const getJobTitle = (job) =>
  firstValue(job?.jobTitle, job?.job_title) || "";

const getStatus = (job) => firstValue(job?.status) || "PENDING";

const getApplicationCount = (job) =>
  firstValue(
    job?.applicationCount,
    job?.application_count,
    job?.applicationsCount,
    job?.applications
  ) ?? 0;

const getCreatedAt = (job) =>
  firstValue(job?.createdAt, job?.created_at);

const normalizeJob = (job) => ({
  ...job,
  jobId: getJobId(job),
  postedById: getPostedById(job),
  jobTitle: getJobTitle(job),
  status: getStatus(job),
  applicationCount: getApplicationCount(job),
  createdAt: getCreatedAt(job),
});

const normalizeJobsResponse = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.content)) return response.content;
  if (Array.isArray(response?.data?.content)) {
    return response.data.content;
  }
  return [];
};

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch (error) {
    console.error("USER PARSE ERROR:", error);
    return {};
  }
};

const getLoggedInUserId = () => {
  const user = getStoredUser();

  const directId =
    localStorage.getItem("userId") ||
    localStorage.getItem("user_id");

  return firstValue(
    directId ? Number(String(directId).replace(/"/g, "")) : null,
    user?.userId,
    user?.user_id,
    user?.id
  );
};

const getLoggedInPartnerId = () => {
  const user = getStoredUser();

  return firstValue(
    user?.partnerId,
    user?.partner?.id,
    user?.userId,
    user?.user_id,
    user?.id
  );
};

const getLoggedInUserType = () => {
  const user = getStoredUser();

  let type =
    user?.userType ||
    user?.user_type ||
    user?.role ||
    localStorage.getItem("userType") ||
    localStorage.getItem("user_type") ||
    localStorage.getItem("role") ||
    "";

  type = String(type).toUpperCase();

  if (type === "RECRUITER") {
    type = "PARTNER";
  }

  return type;
};

const formatNumber = (value) =>
  Number(value ?? 0).toLocaleString("en-IN");

const getRemainingPosts = (usage) => {
  if (!usage) return "-";

  if (
    usage.remainingCount !== undefined &&
    usage.remainingCount !== null
  ) {
    return Number(usage.remainingCount) < 0
      ? "Unlimited"
      : formatNumber(Math.max(0, Number(usage.remainingCount)));
  }

  const limit = Number(usage.allowedLimit ?? 0);
  const used = Number(usage.usedCount ?? 0);

  if (limit < 0) return "Unlimited";

  return formatNumber(Math.max(0, limit - used));
};

const getUsagePercentage = (usage) => {
  if (!usage) return 0;

  const limit = Number(usage.allowedLimit ?? 0);
  const used = Number(usage.usedCount ?? 0);

  if (limit <= 0) return 0;

  return Math.min(100, Math.max(0, (used / limit) * 100));
};

export default function PartnerDashboard() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [companyDetails, setCompanyDetails] = useState(null);
  const [jobs, setJobs] = useState([]);

  const [subscription, setSubscription] = useState(null);
  const [jobPostUsage, setJobPostUsage] = useState(null);
  const [usageError, setUsageError] = useState("");

  // Additional feature quotas
  const [jobInviteUsage, setJobInviteUsage] = useState(null);
  const [resumeDownloadUsage, setResumeDownloadUsage] = useState(null);
  const [profileViewUsage, setProfileViewUsage] = useState(null);

  const loadDashboard = useCallback(async (showRefresh = false) => {
    try {
      if (showRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");
      setUsageError("");

      const user = getStoredUser();
      const email = user?.email;

      if (!email) {
        throw new Error(
          "No logged-in user found. Please log in again."
        );
      }

      const loggedInUserId = getLoggedInUserId();
      const userType = getLoggedInUserType();

      // 1. Find partner by email.
      const partnerRes = await fetch(`${API_BASE}/partners/all`);

      if (!partnerRes.ok) {
        throw new Error("Could not load the partners list.");
      }

      const allPartners = await partnerRes.json();

      const matchedPartner = Array.isArray(allPartners)
        ? allPartners.find(
            (partner) =>
              String(partner?.email || "").toLowerCase().trim() ===
              String(email).toLowerCase().trim()
          )
        : null;

      if (!matchedPartner) {
        throw new Error(
          "Could not find a partner record for this account."
        );
      }

      const resolvedPartnerId =
        matchedPartner?.id ?? matchedPartner?.partnerId;

      if (!resolvedPartnerId) {
        throw new Error(
          "Partner ID missing in the matched partner record."
        );
      }

      // 2. Company details.
      try {
        const companyRes = await fetch(
          `${API_BASE}/company-details/partner/${resolvedPartnerId}`
        );

        if (companyRes.ok) {
          setCompanyDetails(await companyRes.json());
        } else {
          setCompanyDetails(null);
          console.warn(
            "Company details fetch failed:",
            companyRes.status
          );
        }
      } catch (companyErr) {
        console.error("COMPANY DETAILS ERROR:", companyErr);
        setCompanyDetails(null);
      }

      // 3. Fetch jobs and filter to this partner.
      try {
        const response = await jobService.getAllJobs();
        const allJobs = normalizeJobsResponse(response).map(normalizeJob);

        const idToMatch =
          getLoggedInPartnerId() ?? resolvedPartnerId;

        const myJobs = allJobs.filter(
          (job) =>
            Number(job.postedById) === Number(idToMatch)
        );

        setJobs(myJobs);
      } catch (jobsErr) {
        console.error("JOBS FETCH ERROR:", jobsErr);
        setJobs([]);
      }

      // 4. Fetch subscription details and real JOB_POST usage.
      // Existing JOB_POST usage logic is preserved.
      if (loggedInUserId) {
        try {
          const activeSubscription = await getActiveSubscription(
            loggedInUserId
          );

          setSubscription(activeSubscription || null);

          if (activeSubscription) {
            const resolvedUserType =
              userType ||
              String(activeSubscription.userType || "").toUpperCase();

            if (!resolvedUserType) {
              setJobPostUsage(null);
              setUsageError("Unable to identify your account type.");
            } else {
              try {
                const usage = await getActiveFeatureUsage(
                  loggedInUserId,
                  resolvedUserType,
                  "JOB_POST"
                );

                setJobPostUsage(usage || null);

                if (!usage) {
                  setUsageError(
                    "Job-post usage data was not returned."
                  );
                }
              } catch (usageErr) {
                console.error("JOB POST USAGE ERROR:", usageErr);
                setJobPostUsage(null);
                setUsageError(
                  usageErr?.message ||
                    "Unable to load job-post usage."
                );
              }
            }
          } else {
            setJobPostUsage(null);
          }
        } catch (subscriptionErr) {
          console.error(
            "SUBSCRIPTION FETCH ERROR:",
            subscriptionErr
          );
          setSubscription(null);
          setJobPostUsage(null);
          setUsageError(
            subscriptionErr?.message ||
              "Unable to load subscription usage."
          );
        }

        // 5. Fetch the three additional feature quotas.
        // Each request is independent so one failure won't stop the others.
        const featureUsages = [
          ["JOB_INVITE", setJobInviteUsage],
          ["RESUME_DOWNLOAD", setResumeDownloadUsage],
          ["PROFILE_VIEW", setProfileViewUsage],
        ];

        const resolvedUserType =
          userType ||
          String(
            getStoredUser()?.userType ||
              getStoredUser()?.role ||
              "PARTNER"
          ).toUpperCase();

        await Promise.all(
          featureUsages.map(async ([featureCode, setUsage]) => {
            try {
              const usage = await getActiveFeatureUsage(
                loggedInUserId,
                resolvedUserType,
                featureCode
              );

              setUsage(usage || null);
            } catch (featureError) {
              console.error(`${featureCode} usage error:`, featureError);
              setUsage(null);
            }
          })
        );
      } else {
        setSubscription(null);
        setJobPostUsage(null);
        setUsageError("Unable to identify the logged-in user ID.");
        setJobInviteUsage(null);
        setResumeDownloadUsage(null);
        setProfileViewUsage(null);
      }
    } catch (err) {
      console.error("DASHBOARD ERROR:", err);
      setError(err?.message || "Failed to load dashboard data.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const totalJobs = jobs.length;

  const statusCounts = jobs.reduce((acc, job) => {
    const status = job.status || "UNKNOWN";
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const statusChartData = Object.keys(statusCounts).map((status) => ({
    name: status,
    value: statusCounts[status],
  }));

  const applicationsByJob = jobs.map((job) => ({
    name: job.jobTitle || `Job #${job.jobId}`,
    applications: Number(job.applicationCount ?? 0),
  }));

  if (loading) {
    return (
      <div>
        <PartnerHeader />
        <div style={{ padding: 60, textAlign: "center", color: "#666" }}>
          Loading dashboard...
        </div>
        <PartnerFooter />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <PartnerHeader />
        <div style={{ padding: 60, textAlign: "center", color: "#c92a2a" }}>
          {error}
          <div style={{ marginTop: 18 }}>
            <button
              type="button"
              onClick={() => loadDashboard(true)}
              style={buttonStyle}
              disabled={refreshing}
            >
              <RefreshCw size={16} />
              {refreshing ? "Refreshing..." : "Try Again"}
            </button>
          </div>
        </div>
        <PartnerFooter />
      </div>
    );
  }

  const jobPostUsed = Number(jobPostUsage?.usedCount ?? 0);
  const jobPostLimit = jobPostUsage?.allowedLimit;
  const jobPostPercentage = getUsagePercentage(jobPostUsage);
  const unlimited = Number(jobPostLimit ?? 0) < 0;

  return (
    <div style={{ background: THEME.neutral, minHeight: "100vh" }}>
      <PartnerHeader />

      <div style={{ padding: 25, maxWidth: 1400, margin: "0 auto" }}>
        {/* DASHBOARD HEADER */}
        <div style={dashboardHeaderStyle}>
          <div>
            <h1 style={{ margin: 0, color: THEME.primary, fontSize: 26 }}>
              Partner Dashboard
            </h1>
            <p style={{ color: "#666", marginBottom: 0 }}>
              Overview of your jobs and subscription usage.
            </p>
          </div>

          <button
            type="button"
            onClick={() => loadDashboard(true)}
            disabled={refreshing}
            style={buttonStyle}
          >
            <RefreshCw
              size={16}
              className={refreshing ? "dashboard-spin" : ""}
            />
            {refreshing ? "Refreshing..." : "Refresh Dashboard"}
          </button>
        </div>

        {/* COMPANY DETAILS */}
        {companyDetails && (
          <div style={cardStyle}>
            <div style={companyRowStyle}>
              {companyDetails.logoUrl && (
                <img
                  src={companyDetails.logoUrl}
                  alt="Company logo"
                  style={logoStyle}
                />
              )}

              <div>
                <h2 style={companyTitleStyle}>
                  {companyDetails.companyName || "Company"}
                </h2>

                <p style={companyTextStyle}>
                  {companyDetails.email}
                </p>

                {companyDetails.phone && (
                  <p style={companyTextStyle}>
                    {companyDetails.phone}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* JOB POST SUBSCRIPTION USAGE - EXISTING LOGIC PRESERVED */}
        <div style={usageCardStyle}>
          <div style={usageTopStyle}>
            <div style={usageIconStyle}>
              <BriefcaseBusiness size={24} />
            </div>

            <div style={{ flex: 1 }}>
              <div style={usageEyebrowStyle}>SUBSCRIPTION USAGE</div>
              <h2 style={usageTitleStyle}>Job Posts</h2>
              <p style={usageDescriptionStyle}>
                Track job posts used under your active subscription.
              </p>
            </div>

            {jobPostUsage && (
              <div style={usageStatusStyle}>
                <CheckCircle2 size={16} />
                Usage Loaded
              </div>
            )}
          </div>

          {usageError && (
            <div style={usageErrorStyle}>
              <AlertCircle size={18} />
              <div>
                <strong>Usage unavailable</strong>
                <div>{usageError}</div>
              </div>
              <button
                type="button"
                onClick={() => loadDashboard(true)}
                disabled={refreshing}
                style={smallRetryStyle}
              >
                Retry
              </button>
            </div>
          )}

          {!subscription && !usageError && (
            <div style={usageNoticeStyle}>
              No active subscription was found for this account.
            </div>
          )}

          {jobPostUsage && (
            <>
              <div style={usageStatsGridStyle}>
                <UsageStat
                  label="Jobs Used"
                  value={formatNumber(jobPostUsed)}
                />

                <UsageStat
                  label="Plan Limit"
                  value={
                    unlimited
                      ? "Unlimited"
                      : jobPostLimit == null
                        ? "-"
                        : formatNumber(jobPostLimit)
                  }
                />

                <UsageStat
                  label="Remaining"
                  value={getRemainingPosts(jobPostUsage)}
                />
              </div>

              <div style={progressTrackStyle}>
                <div
                  style={{
                    ...progressFillStyle,
                    width: unlimited
                      ? "100%"
                      : `${jobPostPercentage}%`,
                  }}
                />
              </div>

              <div style={progressFooterStyle}>
                <span>
                  {unlimited
                    ? "Unlimited plan"
                    : `${jobPostPercentage.toFixed(0)}% of job-post limit used`}
                </span>

                <span>
                  {jobPostUsage.limitReached
                    ? "Limit reached"
                    : "Usage updated"}
                </span>
              </div>
            </>
          )}

          {!jobPostUsage && !usageError && (
            <div style={usageNoticeStyle}>
              Loading job-post usage...
            </div>
          )}
        </div>

        {/* ADDITIONAL SUBSCRIPTION QUOTAS */}
        <div style={additionalQuotaGridStyle}>
          <FeatureUsageCard
            title="Job Invites"
            description="Track trainer invitations under your plan."
            icon={Users}
            usage={jobInviteUsage}
          />

          <FeatureUsageCard
            title="Resume Downloads"
            description="Track resumes downloaded under your plan."
            icon={Download}
            usage={resumeDownloadUsage}
          />

          <FeatureUsageCard
            title="Profile Views"
            description="Track trainer profile views under your plan."
            icon={Eye}
            usage={profileViewUsage}
          />
        </div>

        {/* SUMMARY STAT CARDS */}
        <div style={statsRowStyle}>
          <StatCard label="Total Jobs" value={totalJobs} />

          {Object.keys(statusCounts).map((status) => (
            <StatCard
              key={status}
              label={status}
              value={statusCounts[status]}
            />
          ))}
        </div>

        {/* CHARTS */}
        <div style={chartsRowStyle}>
          <div style={{ ...cardStyle, flex: "1 1 380px", height: 320 }}>
            <h3 style={chartTitleStyle}>Jobs by Status</h3>

            {statusChartData.length === 0 ? (
              <EmptyState text="No jobs to chart yet." />
            ) : (
              <ResponsiveContainer width="100%" height="85%">
                <PieChart>
                  <Pie
                    data={statusChartData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    label
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell
                        key={entry.name}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>

          <div style={{ ...cardStyle, flex: "2 1 500px", height: 320 }}>
            <h3 style={chartTitleStyle}>Applications per Job</h3>

            {applicationsByJob.length === 0 ? (
              <EmptyState text="No jobs to chart yet." />
            ) : (
              <ResponsiveContainer width="100%" height="85%">
                <BarChart data={applicationsByJob}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11 }}
                    interval={0}
                    angle={-15}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis allowDecimals={false} />
                  <Tooltip />
                  <Bar
                    dataKey="applications"
                    fill={THEME.primary}
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* JOBS TABLE */}
        <div style={{ ...cardStyle, marginTop: 24 }}>
          <h3 style={chartTitleStyle}>My Jobs</h3>

          <div style={{ overflowX: "auto" }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Title</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Applications</th>
                  <th style={thStyle}>Posted On</th>
                </tr>
              </thead>

              <tbody>
                {jobs.length === 0 && (
                  <tr>
                    <td style={tdStyle} colSpan={5}>
                      No jobs posted yet.
                    </td>
                  </tr>
                )}

                {jobs.map((job, index) => (
                  <tr key={job.jobId || index}>
                    <td style={tdStyle}>{index + 1}</td>
                    <td style={tdStyle}>{job.jobTitle}</td>

                    <td style={tdStyle}>
                      <span style={statusBadgeStyle(job.status)}>
                        {job.status || "UNKNOWN"}
                      </span>
                    </td>

                    <td style={tdStyle}>
                      {job.applicationCount ?? 0}
                    </td>

                    <td style={tdStyle}>
                      {job.createdAt
                        ? new Date(job.createdAt).toLocaleDateString("en-IN")
                        : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <PartnerFooter />

      <style>{`
        @keyframes dashboardSpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .dashboard-spin {
          animation: dashboardSpin 1s linear infinite;
        }

        @media (max-width: 640px) {
          .dashboard-usage-stats {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

/* PRESENTATIONAL COMPONENTS */

function UsageStat({ label, value }) {
  return (
    <div style={usageStatStyle}>
      <span style={usageStatLabelStyle}>{label}</span>
      <strong style={usageStatValueStyle}>{value}</strong>
    </div>
  );
}

function FeatureUsageCard({ title, description, icon: Icon, usage }) {
  const used = Number(usage?.usedCount ?? 0);
  const limit = Number(usage?.allowedLimit ?? 0);
  const unlimited = limit < 0;

  const remaining = unlimited
    ? "Unlimited"
    : formatNumber(Math.max(0, limit - used));

  const percentage =
    unlimited || limit <= 0
      ? 0
      : Math.min(100, Math.max(0, (used / limit) * 100));

  return (
    <div style={additionalQuotaCardStyle}>
      <div style={usageTopStyle}>
        <div style={usageIconStyle}>
          <Icon size={22} />
        </div>

        <div style={{ flex: 1 }}>
          <h3 style={usageTitleStyle}>{title}</h3>
          <p style={usageDescriptionStyle}>{description}</p>
        </div>
      </div>

      {!usage ? (
        <div style={usageNoticeStyle}>
          Usage unavailable. Check your active subscription and feature usage record.
        </div>
      ) : (
        <>
          <div style={usageStatsGridStyle}>
            <UsageStat label="Used" value={formatNumber(used)} />

            <UsageStat
              label="Plan Limit"
              value={unlimited ? "Unlimited" : formatNumber(limit)}
            />

            <UsageStat label="Remaining" value={remaining} />
          </div>

          <div style={progressTrackStyle}>
            <div
              style={{
                ...progressFillStyle,
                width: unlimited ? "100%" : `${percentage}%`,
              }}
            />
          </div>

          <div style={progressFooterStyle}>
            <span>
              {unlimited ? "Unlimited plan" : `${percentage.toFixed(0)}% used`}
            </span>

            <span>
              {usage.limitReached ? "Limit reached" : "Usage updated"}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div style={{ ...cardStyle, flex: "1 1 160px", textAlign: "center" }}>
      <div style={{ fontSize: 28, fontWeight: 800, color: THEME.primary }}>
        {value}
      </div>

      <div style={statLabelStyle}>{label}</div>
    </div>
  );
}

function EmptyState({ text }) {
  return <div style={emptyStateStyle}>{text}</div>;
}

/* STYLES */

const cardStyle = {
  background: "#fff",
  borderRadius: 12,
  padding: 20,
  boxShadow: "0 4px 16px rgba(62,15,141,0.08)",
  border: `1px solid ${THEME.neutral}`,
};

const dashboardHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  gap: 16,
  marginBottom: 20,
};

const buttonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  border: "none",
  borderRadius: 8,
  padding: "11px 16px",
  background: THEME.primary,
  color: "#fff",
  fontWeight: 700,
  cursor: "pointer",
};

const companyRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 16,
  flexWrap: "wrap",
};

const logoStyle = {
  width: 64,
  height: 64,
  objectFit: "contain",
  borderRadius: 8,
  border: `1px solid ${THEME.neutral}`,
};

const companyTitleStyle = {
  margin: 0,
  color: THEME.primary,
  fontSize: 20,
};

const companyTextStyle = {
  margin: "4px 0 0",
  color: THEME.secondary,
  fontSize: 13,
};

const usageCardStyle = {
  ...cardStyle,
  marginTop: 20,
  background: "linear-gradient(135deg, #fff 0%, #f8f4ff 100%)",
};

const additionalQuotaGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
  gap: 20,
  marginTop: 20,
};

const additionalQuotaCardStyle = {
  ...cardStyle,
  minWidth: 0,
  background: "linear-gradient(135deg, #fff 0%, #f8f4ff 100%)",
};

const usageTopStyle = {
  display: "flex",
  alignItems: "center",
  gap: 14,
  flexWrap: "wrap",
};

const usageIconStyle = {
  width: 52,
  height: 52,
  borderRadius: 12,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: THEME.primary,
  background: "#eee5ff",
};

const usageEyebrowStyle = {
  color: THEME.secondary,
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: 1,
};

const usageTitleStyle = {
  color: THEME.primary,
  margin: "4px 0",
  fontSize: 21,
};

const usageDescriptionStyle = {
  color: "#666",
  margin: 0,
  fontSize: 13,
};

const usageStatusStyle = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  color: "#218447",
  background: "#e7f8ec",
  padding: "8px 12px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 700,
};

const usageStatsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
  gap: 12,
  marginTop: 24,
};

const usageStatStyle = {
  border: `1px solid ${THEME.neutral}`,
  background: "#fff",
  borderRadius: 10,
  padding: 16,
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

const usageStatLabelStyle = {
  color: "#777",
  fontSize: 12,
  fontWeight: 600,
};

const usageStatValueStyle = {
  color: THEME.primary,
  fontSize: 24,
  fontWeight: 800,
  overflowWrap: "anywhere",
};

const progressTrackStyle = {
  height: 10,
  width: "100%",
  overflow: "hidden",
  borderRadius: 20,
  background: "#e8e0f5",
  marginTop: 22,
};

const progressFillStyle = {
  height: "100%",
  borderRadius: 20,
  background: `linear-gradient(90deg, ${THEME.primary}, ${THEME.secondary})`,
  transition: "width 0.3s ease",
};

const progressFooterStyle = {
  display: "flex",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 8,
  marginTop: 10,
  fontSize: 12,
  color: "#666",
};

const usageErrorStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  marginTop: 20,
  padding: 14,
  color: "#9b2525",
  background: "#fff0f0",
  borderRadius: 8,
  fontSize: 13,
};

const smallRetryStyle = {
  marginLeft: "auto",
  padding: "7px 12px",
  border: "1px solid #d7b0b0",
  borderRadius: 6,
  background: "#fff",
  color: "#9b2525",
  cursor: "pointer",
  fontWeight: 700,
};

const usageNoticeStyle = {
  marginTop: 20,
  padding: 14,
  background: "#f8f5ff",
  color: THEME.primary,
  borderRadius: 8,
  fontSize: 13,
};

const statsRowStyle = {
  display: "flex",
  gap: 16,
  marginTop: 20,
  flexWrap: "wrap",
};

const chartsRowStyle = {
  display: "flex",
  gap: 20,
  marginTop: 24,
  flexWrap: "wrap",
};

const chartTitleStyle = {
  margin: "0 0 12px",
  color: THEME.primary,
  fontSize: 15,
  fontWeight: 800,
};

const statLabelStyle = {
  fontSize: 12,
  color: THEME.secondary,
  marginTop: 4,
  textTransform: "uppercase",
  letterSpacing: 0.5,
};

const emptyStateStyle = {
  height: "85%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#999",
  fontSize: 13,
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  fontSize: 13,
};

const thStyle = {
  textAlign: "left",
  padding: "10px 12px",
  borderBottom: `2px solid ${THEME.neutral}`,
  color: THEME.primary,
  fontWeight: 800,
  fontSize: 11,
  textTransform: "uppercase",
};

const tdStyle = {
  padding: "10px 12px",
  borderBottom: `1px solid ${THEME.neutral}`,
  color: "#444",
};

function statusBadgeStyle(status) {
  const map = {
    OPEN: { bg: "#e7f8ec", color: "#1a8a3d" },
    APPROVED: { bg: "#e7f8ec", color: "#1a8a3d" },
    CLOSED: { bg: "#fdeaea", color: "#c92a2a" },
    REJECTED: { bg: "#fdeaea", color: "#c92a2a" },
    BLOCKED: { bg: "#fdeaea", color: "#c92a2a" },
    PENDING: { bg: "#fbf8e8", color: "#8a7f1f" },
  };

  const current = map[status] || {
    bg: THEME.neutral,
    color: THEME.primary,
  };

  return {
    background: current.bg,
    color: current.color,
    padding: "3px 10px",
    borderRadius: 20,
    fontSize: 11,
    fontWeight: 700,
  };
}
